import {
    Statsig,
    StatsigOptions,
    StatsigUser
} from '@statsig/statsig-node-core'

// Must match src/analytics/statsig/statsigConfig.ts (STATSIG_ENVIRONMENT_TIER)
const STATSIG_ENVIRONMENT_TIER = 'production' as const

let statsigServer: Statsig | null = null

export const initializeStatsig = async () => {
    if (typeof window !== 'undefined') {
        return null
    }

    const serverKey = process.env.STATSIG_SERVER_KEY

    if (!serverKey) {
        console.warn('[Statsig] Server key not provided, skipping initialization')
        return null
    }

    if (statsigServer) {
        return statsigServer
    }

    try {
        console.log(
            '[Statsig] Starting initialization with server key:',
            serverKey.substring(0, 20) + '...'
        )
        const options: StatsigOptions = {
            environment: STATSIG_ENVIRONMENT_TIER,
            initTimeoutMs: 2000
        }
        statsigServer = new Statsig(serverKey, options)
        await statsigServer.initialize()
        console.log('[Statsig] ✅ Statsig server initialized')
        return statsigServer
    } catch (error: any) {
        console.error('[Statsig] ❌ Failed to initialize Statsig server:', error)
        console.error('[Statsig] Error details:', error?.message, error?.stack)
        statsigServer = null
        return null
    }
}

export const getStatsigServer = () => {
    return statsigServer
}

// Re-export StatsigUser for convenience
export { StatsigUser }

export interface StatsigUserIdentifiers {
    stableID?: string
    userID?: string
    [key: string]: string | undefined
}

export interface GetExperimentOptions {
    userIdentifiers: StatsigUserIdentifiers
    experimentName: string
    paramName: string
    fallback: string
}

/**
 * Get an experiment value from Statsig on the server side.
 * This should be called during SSR (e.g., in getInitialProps or getServerSideProps).
 *
 * @example
 * const variant = getServerExperiment({
 *   userIdentifiers: { stableID: req.statsigStableId },
 *   experimentName: 'h_exp_dom',
 *   paramName: 'rank_model',
 *   fallback: 'data_engg_model'
 * });
 */
export const getServerExperiment = <T extends string = string>(
    options: GetExperimentOptions
): T => {
    const { userIdentifiers, experimentName, paramName, fallback } = options

    if (!statsigServer) {
        console.warn(
            `[Statsig] Server not initialized, returning fallback for experiment: ${experimentName}`
        )
        return fallback as T
    }

    try {
        const user = StatsigUser.withCustomIDs(
            userIdentifiers as Record<string, string>
        )

        const experiment = statsigServer.getExperiment(user, experimentName)
        const value = experiment.getValue(paramName, fallback)

        return (value ?? fallback) as T
    } catch (error) {
        console.error(`[Statsig] Error getting experiment ${experimentName}:`, error)
        return fallback as T
    }
}

/**
 * Check a feature gate on the server side.
 *
 * @example
 * const isEnabled = checkServerGate({
 *   userIdentifiers: { stableID: req.statsigStableId },
 *   gateName: 'enable_new_checkout'
 * });
 */
export const checkServerGate = (options: {
    userIdentifiers: StatsigUserIdentifiers
    gateName: string
}): boolean => {
    const { userIdentifiers, gateName } = options

    if (!statsigServer) {
        console.warn(
            `[Statsig] Server not initialized, returning false for gate: ${gateName}`
        )
        return false
    }

    try {
        const user = StatsigUser.withCustomIDs(
            userIdentifiers as Record<string, string>
        )

        return statsigServer.checkGate(user, gateName)
    } catch (error) {
        console.error(`[Statsig] Error checking gate ${gateName}:`, error)
        return false
    }
}

/**
 * Get client initialization response for bootstrapping the client SDK.
 * This allows the client to initialize instantly without making an API call.
 *
 * @example
 * const initResponse = getStatsigClientBootstrapValues({ stableID: req.statsigStableId });
 * // Pass this to client via Redux state
 */
export const getStatsigClientBootstrapValues = (
    userIdentifiers: StatsigUserIdentifiers
): string | null => {
    if (!statsigServer) {
        console.warn(
            '[Statsig] Server not initialized, cannot generate client bootstrap values'
        )
        return null
    }

    try {
        const user = StatsigUser.withCustomIDs(
            userIdentifiers as Record<string, string>
        )

        // Returns a JSON string that can be passed to the client SDK
        const clientInitResponse = statsigServer.getClientInitializeResponse(user)
        return clientInitResponse
    } catch (error: any) {
        console.error('[Statsig] Error generating client bootstrap values:', error)
        console.error('[Statsig] Error stack:', error?.stack)
        return null
    }
}
